import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

const STREAMS = ['А', 'Б', 'В', 'Г'];

export default function ClassEditModal({ mode, cls, yearId, years, onClose, onSaved, onDeleted }) {
  const isEdit = mode === 'edit';

  const [data, setData] = useState(() => {
    if (isEdit) return {
      yearId:       cls.yearId,
      stage:        cls.stage || 'primary',
      number:       cls.number !== null && cls.number !== undefined ? String(cls.number) : '',
      stream:       cls.stream || '',
      code:         cls.code || '',
      name:         cls.name || '',
      capacity:     cls.capacity || '',
      room:         cls.room || '',
      ageRange:     cls.ageRange || '',
      teacherId:    cls.teacherId || '',
      assistantId:  cls.assistantId || '',
      archive:      !!cls.archive,
      noVacations:  !!cls.noVacations,
    };
    return {
      yearId:       yearId || (years[0]?.id ?? null),
      stage:        'primary',
      number:       '',
      stream:       '',
      code:         '',
      name:         '',
      capacity:     '',
      room:         '',
      ageRange:     '',
      teacherId:    '',
      assistantId:  '',
      archive:      false,
      noVacations:  false,
    };
  });

  const [teachers, setTeachers] = useState([]);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [refsBlock, setRefsBlock] = useState(null);
  const [codeManual, setCodeManual] = useState(isEdit && !!cls.code);

  // Загружаем сотрудников (учителя + админы с can_teach=1) для пикера классрука
  useEffect(() => {
    Promise.all([
      api.get('/api/staff?role=teacher&active=1'),
      api.get('/api/staff?role=administrator&active=1'),
    ]).then(([t, a]) => {
      const list = [
        ...(t.rows || []),
        ...((a.rows || []).filter((u) => u.canTeach)),
      ];
      list.sort((x, y) => x.name.localeCompare(y.name, 'ru'));
      setTeachers(list);
    }).catch(() => setTeachers([]));
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, saving]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Авто-генерация кода для primary: number+stream → "1А"
  useEffect(() => {
    if (data.stage !== 'primary') return;
    if (codeManual) return;
    if (data.number !== '' && data.stream) {
      set('code', String(data.number) + data.stream);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.stage, data.number, data.stream]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setRefsBlock(null);

    const payload = {
      yearId:      Number(data.yearId),
      stage:       data.stage,
      number:      data.stage === 'primary' && data.number !== '' ? Number(data.number) : null,
      stream:      data.stage === 'primary' ? (data.stream || null) : null,
      code:        data.code.trim(),
      name:        data.name.trim() || null,
      capacity:    data.capacity === '' ? 0 : Number(data.capacity),
      room:        data.room.trim() || null,
      ageRange:    data.ageRange.trim() || null,
      teacherId:   data.teacherId ? Number(data.teacherId) : null,
      assistantId: data.assistantId ? Number(data.assistantId) : null,
      archive:     data.archive,
      noVacations: data.noVacations,
    };

    setSaving(true);
    try {
      const res = isEdit
        ? await api.put(`/api/classes/${cls.id}`, payload)
        : await api.post('/api/classes', payload);
      onSaved?.(res.class);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось сохранить'));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!isEdit) return;
    if (!confirm(`Удалить класс «${cls.code}»?`)) return;
    setSaving(true); setError(null); setRefsBlock(null);
    try {
      await api.delete(`/api/classes/${cls.id}`);
      onDeleted?.(cls.id);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'in_use' && err.payload?.error?.references) {
        setRefsBlock(err.payload.error);
      } else {
        setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось удалить'));
      }
      setSaving(false);
    }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !saving) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{isEdit ? `Класс — ${cls.code}` : 'Новый класс / группа'}</h2>
          <button className="sd-close" onClick={onClose} disabled={saving}><Icon name="x" size={14} /></button>
        </div>
        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field">
              <label className="sd-edit__label">Учебный год *</label>
              <select className="sd-edit__select" value={data.yearId || ''} onChange={(e) => set('yearId', e.target.value)} required>
                <option value="">— выберите —</option>
                {years.map((y) => <option key={y.id} value={y.id}>{y.name}</option>)}
              </select>
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Ступень *</label>
              <select
                className="sd-edit__select"
                value={data.stage}
                onChange={(e) => {
                  const v = e.target.value;
                  set('stage', v);
                  if (v === 'kindergarten') {
                    set('number', '');
                    set('stream', '');
                  }
                }}
                disabled={isEdit}
                title={isEdit ? 'Ступень нельзя изменить после создания' : undefined}
              >
                <option value="primary">Начальная школа</option>
                <option value="kindergarten">Детский сад</option>
              </select>
            </div>

            {data.stage === 'primary' ? (
              <>
                <div className="sd-edit__field">
                  <label className="sd-edit__label">Класс (0–4) *</label>
                  <select
                    className="sd-edit__select"
                    value={data.number}
                    onChange={(e) => set('number', e.target.value)}
                    required
                  >
                    <option value="">—</option>
                    {[0, 1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div className="sd-edit__field">
                  <label className="sd-edit__label">Параллель *</label>
                  <select
                    className="sd-edit__select"
                    value={data.stream}
                    onChange={(e) => set('stream', e.target.value)}
                    required
                  >
                    <option value="">—</option>
                    {STREAMS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </>
            ) : null}

            <div className="sd-edit__field">
              <label className="sd-edit__label">Код *</label>
              <input
                className="sd-edit__input mono"
                value={data.code}
                onChange={(e) => { set('code', e.target.value); setCodeManual(true); }}
                placeholder={data.stage === 'kindergarten' ? 'ДСА' : '1А'}
                required
              />
              <div className="sd-edit__hint">
                Короткий идентификатор. {data.stage === 'primary' ? 'По умолчанию формируется автоматически из класса+параллели.' : 'Например, ДСА, ДСБ.'}
              </div>
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Имя {data.stage === 'kindergarten' ? '*' : ''}</label>
              <input
                className="sd-edit__input"
                value={data.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder={data.stage === 'kindergarten' ? 'Старшая «Солнышко»' : 'оставьте пустым — будет «1 «А»»'}
                required={data.stage === 'kindergarten'}
              />
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">Наполняемость</label>
              <input
                className="sd-edit__input"
                type="number"
                min="0"
                value={data.capacity}
                onChange={(e) => set('capacity', e.target.value)}
                placeholder="0 = без лимита"
              />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Кабинет</label>
              <input
                className="sd-edit__input"
                value={data.room}
                onChange={(e) => set('room', e.target.value)}
                placeholder="Корп. 1, к. 105"
              />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Возраст</label>
              <input
                className="sd-edit__input"
                value={data.ageRange}
                onChange={(e) => set('ageRange', e.target.value)}
                placeholder="6–7 лет"
              />
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">Классный руководитель</label>
              <select
                className="sd-edit__select"
                value={data.teacherId || ''}
                onChange={(e) => set('teacherId', e.target.value)}
              >
                <option value="">— не назначен —</option>
                {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}{t.role === 'administrator' ? ' (админ)' : ''}</option>)}
              </select>
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Ассистент / помощник</label>
              <select
                className="sd-edit__select"
                value={data.assistantId || ''}
                onChange={(e) => set('assistantId', e.target.value)}
              >
                <option value="">— не назначен —</option>
                {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}{t.role === 'administrator' ? ' (админ)' : ''}</option>)}
              </select>
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="staff-auth-row">
                <input type="checkbox" checked={data.archive} onChange={(e) => set('archive', e.target.checked)} />
                <span>Архив</span>
              </label>
              <label className="staff-auth-row" style={{ marginTop: 6 }}>
                <input
                  type="checkbox"
                  checked={data.noVacations}
                  onChange={(e) => set('noVacations', e.target.checked)}
                />
                <span>Нет каникул</span>
              </label>
            </div>
          </div>

          {refsBlock && (
            <div className="sd-edit__error" style={{ margin: '0 20px 12px' }}>
              <strong>{refsBlock.message}</strong>
            </div>
          )}

          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            {isEdit && (
              <button type="button" className="btn btn--sm btn--danger" onClick={onDelete} disabled={saving}>Удалить</button>
            )}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose} disabled={saving}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
              {saving ? 'Сохраняем…' : (isEdit ? 'Сохранить' : 'Создать')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
